export function videoTrackLooksLikeScreenShare(track: MediaStreamTrack): boolean {
  if (track.kind !== 'video') return false;

  const settings = track.getSettings() as MediaTrackSettings & { displaySurface?: string };
  if (
    settings.displaySurface === 'monitor' ||
    settings.displaySurface === 'window' ||
    settings.displaySurface === 'browser' ||
    settings.displaySurface === 'application'
  ) {
    return true;
  }

  if (track.contentHint === 'detail' || track.contentHint === 'text') {
    return true;
  }

  const label = track.label.toLowerCase();
  return (
    label.includes('screen') ||
    label.includes('display') ||
    label.includes('window') ||
    label.includes('desktop') ||
    label.includes('tab')
  );
}

export function videoTrackLooksLikeCamera(track: MediaStreamTrack): boolean {
  if (track.kind !== 'video') return false;
  const label = track.label.toLowerCase();
  return (
    label.includes('camera') ||
    label.includes('facetime') ||
    label.includes('front') ||
    label.includes('back') ||
    label.includes('iphone') ||
    label.includes('integrated')
  );
}

/** Screen shares are typically higher resolution than camera previews. */
function pickLikelyScreenTrack(tracks: MediaStreamTrack[]): MediaStreamTrack {
  if (tracks.length === 1) return tracks[0];
  return tracks.reduce((best, track) => {
    const bestW = best.getSettings().width ?? 0;
    const trackW = track.getSettings().width ?? 0;
    if (trackW !== bestW) return trackW > bestW ? track : best;
    const bestH = best.getSettings().height ?? 0;
    const trackH = track.getSettings().height ?? 0;
    return trackH > bestH ? track : best;
  });
}

export function streamLooksLikeScreenShare(stream: MediaStream | null): boolean {
  if (!stream) return false;
  return stream.getVideoTracks().some((track) => videoTrackLooksLikeScreenShare(track));
}

export type SplitStreamOptions = {
  /** When true, apply fallbacks for remote tracks missing displaySurface metadata. */
  screenShareSignaled?: boolean;
};

export type SplitMediaStreams = {
  camera: MediaStream | null;
  screen: MediaStream | null;
  audio: MediaStream | null;
  combined: MediaStream | null;
};

/** Split a remote/local stream into camera, screen, and audio for rendering. */
export function splitStreamByTrackType(
  stream: MediaStream | null,
  options?: SplitStreamOptions,
): SplitMediaStreams {
  if (!stream) {
    return { camera: null, screen: null, audio: null, combined: null };
  }

  const cameraTracks: MediaStreamTrack[] = [];
  const screenTracks: MediaStreamTrack[] = [];
  const audioTracks: MediaStreamTrack[] = [];
  const liveVideoTracks: MediaStreamTrack[] = [];

  for (const track of stream.getTracks()) {
    if (track.readyState !== 'live') continue;
    if (track.kind === 'audio') {
      audioTracks.push(track);
    } else if (track.kind === 'video') {
      liveVideoTracks.push(track);
      if (videoTrackLooksLikeScreenShare(track)) {
        screenTracks.push(track);
      } else if (!videoTrackLooksLikeCamera(track)) {
        // Unknown video — defer classification until fallback pass
      } else {
        cameraTracks.push(track);
      }
    }
  }

  // Assign unknown video tracks (remote screen tracks often lack displaySurface)
  for (const track of liveVideoTracks) {
    if (screenTracks.includes(track) || cameraTracks.includes(track)) continue;
    if (options?.screenShareSignaled) {
      screenTracks.push(track);
    } else {
      cameraTracks.push(track);
    }
  }

  if (options?.screenShareSignaled && screenTracks.length === 0 && liveVideoTracks.length > 0) {
    const screenTrack = pickLikelyScreenTrack(liveVideoTracks);
    screenTracks.push(screenTrack);
    for (const track of liveVideoTracks) {
      if (track.id !== screenTrack.id && !cameraTracks.includes(track)) {
        cameraTracks.push(track);
      }
    }
  } else if (options?.screenShareSignaled && screenTracks.length > 0 && cameraTracks.length === 0) {
    const likelyCamera = liveVideoTracks.find(
      (t) => !screenTracks.includes(t) && videoTrackLooksLikeCamera(t),
    );
    if (likelyCamera) {
      screenTracks.splice(screenTracks.indexOf(likelyCamera), 1);
      cameraTracks.push(likelyCamera);
    } else if (liveVideoTracks.length > 1) {
      const screenTrack = pickLikelyScreenTrack(liveVideoTracks);
      screenTracks.length = 0;
      cameraTracks.length = 0;
      screenTracks.push(screenTrack);
      for (const track of liveVideoTracks) {
        if (track.id !== screenTrack.id) cameraTracks.push(track);
      }
    }
  }

  const camera = cameraTracks.length ? new MediaStream(cameraTracks) : null;
  const screen = screenTracks.length ? new MediaStream(screenTracks) : null;
  const audio = audioTracks.length ? new MediaStream(audioTracks) : null;

  return { camera, screen, audio, combined: stream };
}

export function resolveRemoteScreenStream(
  stream: MediaStream | null,
  screenShareSignaled: boolean,
): MediaStream | null {
  const split = splitStreamByTrackType(stream, { screenShareSignaled });
  if (split.screen) return split.screen;
  if (!screenShareSignaled || !stream) return null;
  const live = stream.getVideoTracks().filter((t) => t.readyState === 'live');
  if (!live.length) return null;
  return new MediaStream([pickLikelyScreenTrack(live)]);
}

export function extractVideoStream(stream: MediaStream | null): MediaStream | null {
  if (!stream) return null;
  const tracks = stream.getVideoTracks().filter((t) => t.readyState === 'live' && t.enabled);
  return tracks.length ? new MediaStream(tracks) : null;
}

export function isParticipantScreenSharing(
  isLocal: boolean,
  localScreenEnabled: boolean,
  remoteScreenSignaled: boolean | undefined,
  remoteScreenEnabled: boolean | undefined,
  stream: MediaStream | null,
): boolean {
  if (isLocal) return localScreenEnabled;
  if (remoteScreenSignaled) return true;
  if (remoteScreenEnabled && streamLooksLikeScreenShare(stream)) return true;
  return false;
}
